import { useEffect, useRef } from 'react';

import { useLocation } from 'react-router-dom';

import { useGetBillingProfile } from 'entities/billing/api/useGetBillingProfile';
import type { OnboardingStateResponse } from 'entities/onboarding/types';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { useAuth } from 'shared/lib/auth/useAuth';
import { localStorageService, StorageKeys } from 'shared/lib/storage/storageService';
import { urls } from 'shared/urls';

// Ids of users we've already auto-opened for, persisted so it happens only once ever (not on every
// refresh/session); afterwards the user opens it manually via the launcher.
function hasAutoOpened(userId: string): boolean {
  const seen = localStorageService.get(StorageKeys.ONBOARDING_AUTO_OPENED);
  return Array.isArray(seen) && seen.includes(userId);
}

function markAutoOpened(userId: string): void {
  const seen = localStorageService.get(StorageKeys.ONBOARDING_AUTO_OPENED);
  const ids = Array.isArray(seen) ? seen : [];
  if (!ids.includes(userId)) {
    localStorageService.set(StorageKeys.ONBOARDING_AUTO_OPENED, [...ids, userId]);
  }
}

type Options = {
  /** False while app access or the assistant flag are unresolved or denied. */
  enabled: boolean;
  /** Undefined until the state resolves; auto-open waits for a definite answer rather than guessing. */
  onboardingState: OnboardingStateResponse | undefined;
};

/**
 * Opens the onboarding dialog once, the first time a user is eligible for it.
 *
 * Kept out of the dialog component because deciding *when* to open involves the subscription wizard,
 * the billing profile and persisted state, none of which the dialog needs in order to render.
 */
export function useAutoOpenOnboarding({ enabled, onboardingState }: Options): void {
  const { user } = useAuth();
  const open = useOnboardingDialogStore((state) => state.open);
  const autoOpenedRef = useRef(false);

  // Self-serve clients get nudged through the post-signup subscription wizard first (see the
  // Auth0Context redirect, gated on the same can_subscribe signal). We don't open the assistant on
  // top of that wizard; instead we open it once the user *leaves* the wizard the first time — whether
  // they finished all steps or exited early. Other account types have no such wizard, so they open on
  // first load as before.
  const isSelfServe = Boolean(user?.isCompanySelfServe);
  const { data: billingProfile } = useGetBillingProfile({ enabled: enabled && isSelfServe });
  const subscriptionWizardPending = isSelfServe && billingProfile?.can_subscribe !== false;

  const onSubscriptionWizard = useLocation().pathname === urls.subscriptionFlow.url();
  const visitedWizardRef = useRef(false);
  if (onSubscriptionWizard) {
    visitedWizardRef.current = true;
  }

  // Absent on an older backend, which is the pre-enrollment behaviour: show the checklist.
  const checklistEnabled = onboardingState && onboardingState.checklist_enabled !== false;

  useEffect(() => {
    if (autoOpenedRef.current || !user || !enabled || typeof window === 'undefined') {
      return;
    }
    // Auto-opening exists to walk someone through onboarding. Members who joined before the
    // checklist shipped have nothing to be walked through, so they only ever open it themselves.
    // Undefined means the state hasn't resolved yet, so this waits rather than assuming enabled.
    if (!checklistEnabled) {
      return;
    }
    // Never while the user is still in the wizard.
    if (onSubscriptionWizard) {
      return;
    }
    // A self-serve user who still owes the wizard but hasn't entered it yet is about to be
    // redirected there — hold off until they've been through and left it.
    if (subscriptionWizardPending && !visitedWizardRef.current) {
      return;
    }
    if (hasAutoOpened(user.id)) {
      return;
    }
    markAutoOpened(user.id);
    autoOpenedRef.current = true;
    open();
  }, [user, enabled, checklistEnabled, onSubscriptionWizard, subscriptionWizardPending, open]);
}
